'use client';

import {
  District,
  Image,
  Person,
  TConfiguration,
  TPosition,
  filterEntitiesBySubscription,
  randomizePeopleBySlots,
  useRows,
} from '@nettbureau/featured-core';
import { TranslationProvider } from '@nettbureau/featured-translations';
import { Provider } from '@nettbureau/vibrant-ui';
import { useTranslate } from '@tolgee/react';
import { PageProps } from 'gatsby';
import { buildGoogleMapsProps } from '../../functions';
import { ClientSideRender } from '../ClientSideRender';
import { GoogleMap } from '../map/GoogleMap';
import { ConfigProvider } from '../provider/config-provider';
import { BasicCompaniesSection } from '../sections/BasicCompaniesSection';
import { CompaniesSection } from '../sections/CompaniesSection';
import { PeopleSection } from '../sections/PeopleSection';
import { HeadForLocation } from '../shared/Head';
import { RenderPosition } from '../shared/RenderPositions';

export interface DistrictPageProps {
  district: District;
  people: Person[][];
  language: string;
  contactUrl: string | null;
  catalogConfiguration: TConfiguration;
  positions: TPosition[];
  defaultBackground: Image | null;
}

export default function GatsbyPage(
  props: PageProps<object, DistrictPageProps>,
) {
  return (
    <ClientSideRender>
      <Provider background={null}>
        <TranslationProvider language={props.pageContext.language}>
          <Render {...props.pageContext} />
        </TranslationProvider>
      </Provider>
    </ClientSideRender>
  );
}

function Render({
  district,
  contactUrl,
  people,
  defaultBackground,
  catalogConfiguration,
  positions,
}: DistrictPageProps) {
  const {
    companies,
    offices,
    configuration: { companyContainerTile, personContainerTitle },
  } = district;
  const { cardVisibleRows } = catalogConfiguration;

  const companyRows = useRows({
    visibility: 'row',
    sort: 'random',
    visibleRows: cardVisibleRows,
    items: filterEntitiesBySubscription([...companies, ...offices], 'premium'),
  });

  const personRows = useRows<Person>({
    items: filterEntitiesBySubscription(
      randomizePeopleBySlots(people),
      'premium',
    ),
    visibleRows: cardVisibleRows,
    visibility: 'row',
    sort: 'random',
  });

  const markers = buildGoogleMapsProps([...companies, ...offices]);

  const { t } = useTranslate();

  return (
    <RenderPosition
      positions={positions}
      render={({ component, variant }) => {
        switch (component) {
          case 'Products':
          case 'City':
          case 'District':
            return null;
          case 'BasicCompanies':
            return (
              <BasicCompaniesSection
                companies={filterEntitiesBySubscription(
                  [...companies, ...offices],
                  'basic',
                )}
                deadCompanies={filterEntitiesBySubscription(
                  [...companies, ...offices],
                  'graveyard',
                )}
                nonClientCompanies={filterEntitiesBySubscription(
                  [...companies, ...offices],
                  'non-client',
                )}
              />
            );
          case 'TrustFactors':
            return null;
          case 'Map':
            return markers ? (
              <GoogleMap maxZoom={19} markerDefinitions={markers} />
            ) : null;
          case 'People':
            return (
              <ConfigProvider
                value={{
                  globalThemeVariant: variant,
                  ...catalogConfiguration,
                }}
              >
                <PeopleSection
                  defaultBackground={defaultBackground}
                  people={personRows}
                  containerTitle={personContainerTitle ?? t('featured_people')}
                />
              </ConfigProvider>
            );
          case 'Companies':
            return (
              <ConfigProvider
                value={{
                  globalThemeVariant: variant,
                  ...catalogConfiguration,
                }}
              >
                <CompaniesSection
                  contactUrl={contactUrl}
                  defaultBackground={defaultBackground}
                  companies={companyRows}
                  title={companyContainerTile ?? t('featured_companies')}
                />
              </ConfigProvider>
            );
        }
      }}
    />
  );
}

export const Head = (props: PageProps<object, DistrictPageProps>) => {
  return <HeadForLocation name={props.pageContext.district.name} />;
};
